import { FormRenderProps } from '@progress/kendo-react-form';
import {
  buildCurrentFormValues,
  separateFileMetadata,
  updateFilesWithPerFileMetadata
} from './FieldInteractionsHelper';
import type { UseFieldInteractionApiReturn } from './useFieldInteractionApi';
import { ContentTypeFieldResponse, LoadFieldValuesFromFileRequest } from '@cs-cms/types';
import type { useFieldState } from './useFieldState';

type FieldState = ReturnType<typeof useFieldState>;

interface CreateFileFieldChangeHandlerParams {
  ctFields: ContentTypeFieldResponse[];
  selectedCtName?: string;
  fieldState: FieldState;
  applyLookupResponseToForm: UseFieldInteractionApiReturn['applyLookupResponseToForm'];
  contentTypeUsesFilesProcessor?: boolean;
  loadFieldValuesFromFile: (request: LoadFieldValuesFromFileRequest) => Promise<{
    valuesForOtherFields?: Record<string, unknown>;
  } | null | undefined>;
}

export const createFileFieldChangeHandler = ({
  ctFields,
  selectedCtName,
  fieldState,
  applyLookupResponseToForm,
  contentTypeUsesFilesProcessor,
  loadFieldValuesFromFile
}: CreateFileFieldChangeHandlerParams) => {
  return async (fieldName: string, files: FileList, formRenderProps: FormRenderProps): Promise<void> => {
    if (!files?.length || !contentTypeUsesFilesProcessor || !selectedCtName) {
      return;
    }

    fieldState.addLoadingField(fieldName);

    try {
      const currentValues = buildCurrentFormValues(fieldName, null, formRenderProps, ctFields);

      const request: LoadFieldValuesFromFileRequest = {
        data: {
          contentTypeName: selectedCtName,
          metadata: currentValues
        },
        fileList: Array.from(files)
      };

      const response = await loadFieldValuesFromFile(request);

      if (!response?.valuesForOtherFields) {
        return;
      }

      const perFileFields = ctFields.filter(f => f.fileDependent === true);

      if (perFileFields.length > 0) {
        const perFileFieldNames = new Set(perFileFields.map(f => f.name));
        const { perFileMetadata, sharedMetadata } = separateFileMetadata(
          response.valuesForOtherFields,
          perFileFieldNames
        );

        const currentFiles = formRenderProps.valueGetter(fieldName) || [];
        const updatedFiles = updateFilesWithPerFileMetadata(currentFiles, perFileMetadata);

        formRenderProps.onChange(fieldName, { value: updatedFiles });
        await applyLookupResponseToForm({ valuesForOtherFields: sharedMetadata }, formRenderProps);
      } else {
        await applyLookupResponseToForm(response, formRenderProps);
      }
    } catch {
      // empty
    } finally {
      fieldState.removeLoadingField(fieldName);
    }
  };
};
